import { loadRemoteModule } from '@angular-architects/module-federation';
import { Injectable } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { Observable, map } from 'rxjs';
import { Result } from 'src/app/application/interfaces/result.interface';
import { MfListServiceUseCase } from 'src/app/application/usesCases/mf-list.service';
import { MfListDataModules } from 'src/app/domain/models/mf-list.model';
import { ERoles } from '../enums/roles';
import { AuthGuard } from '../guards/auth.guard';
import { ModuleWithContainer } from '../interfaces/module.interface';
import { ModulesGuard } from './../guards/modules.guard';
import { AuthDataService } from './auth-data.service';

@Injectable({
  providedIn: 'root',
})
export class RoutingService {
  public microFrontsList: MfListDataModules[] = [];
  public mfRoutesGenerated: any;
  constructor(
    private mfListService: MfListServiceUseCase,
    public authDataService: AuthDataService,
    private router: Router
  ) {}

  configInitialRoutes() {
    this.getModuleRoutes().subscribe({
      next: (dataRoutes) => {
        this.mfRoutes = this.createRoutes(dataRoutes);
        this.saveOptions(dataRoutes);
        const modules: ModuleWithContainer[] = [];

        dataRoutes.forEach((mf: MfListDataModules) => {
          const module = this.getModule(dataRoutes, mf.name);
          if (module) {
            modules.push(module);
          }
        });

        this.navigateToModule(modules);
      },
    });
  }

  getModule(
    dataRoutes: MfListDataModules[],
    nameModule: string
  ): ModuleWithContainer | undefined {
    const module = dataRoutes.find((mf) => mf.name === nameModule && mf.active);
    if (!module) {
      const container = dataRoutes.find((container) =>
        container.children?.some((child) => child.name === nameModule)
      );
      const childModule = container?.children?.find(
        (child) => child.name === nameModule
      );
      if (childModule && container && childModule.active) {
        return { module: childModule, containerSlug: container.slug };
      }
    } else {
      return { module, containerSlug: '' };
    }
    return undefined;
  }

  navigateToModule(modules: ModuleWithContainer[]): void {
    const routeToRedirect = this.getRouteToRedirect(modules);
    this.router.navigateByUrl(routeToRedirect);
  }

  getRouteToRedirect(modules: ModuleWithContainer[]): string {
    const initialRoute = '/dashboard';
    const idRole = this.authDataService.getUserData().idRole;

    const bonoMf = modules.find(
      (module) => module.module.name === 'Bonos' && idRole === ERoles.cashier
    );

    if (bonoMf) {
      return bonoMf.containerSlug
        ? `${initialRoute}/${bonoMf.containerSlug}/${bonoMf.module.slug}`
        : `${initialRoute}/${bonoMf.module.slug}`;
    }

    if (modules.length > 0) {
      const firstModule = modules[0];
      return firstModule.containerSlug
        ? `${initialRoute}/${firstModule.containerSlug}/${firstModule.module.slug}`
        : firstModule.module.children
        ? `${initialRoute}/${firstModule.module.slug}/${
            firstModule.module.children.find(
              (childrenModule) => childrenModule.active
            )?.slug
          }`
        : `${initialRoute}/${firstModule.module.slug}`;
    }

    return initialRoute;
  }

  createRoutes(modules: MfListDataModules[] | null): Routes {
    if (modules == null || modules.length === 0) {
      return [];
    }

    const lazyRoutes: Routes = modules.flatMap((module) => {
      const routes: Routes = [];
      if (module.hasChildren && module.children) {
        const childrenRoutes: Routes = module.children
          .filter(
            (subModule) =>
              subModule.mfurl && subModule.mfurl !== 'null' && subModule.active
          )
          .map((subModule) => ({
            path: subModule.slug,
            canActivate: [AuthGuard],
            canLoad: [ModulesGuard],
            loadChildren: () =>
              loadRemoteModule({
                type: 'module',
                remoteEntry: subModule.mfurl as string,
                exposedModule: './Module',
              }).then((m) => m['UiModule']),
          }));
        if (childrenRoutes.length > 0) {
          routes.push({
            path: module.slug,
            children: childrenRoutes,
          });
        }
      } else if (module.mfurl && module.mfurl !== 'null' && module.active) {
        routes.push({
          path: module.slug,
          canActivate: [AuthGuard],
          canLoad: [ModulesGuard],
          loadChildren: () =>
            loadRemoteModule({
              type: 'module',
              remoteEntry: module.mfurl as string,
              exposedModule: './Module',
            }).then((m) => m['UiModule']),
        });
      }
      return routes;
    });
    return lazyRoutes;
  }

  getModuleRoutes(): Observable<MfListDataModules[]> {
    const idCommerce = this.authDataService.getUserData().idCommerce.toString();
    const idRole = this.authDataService.getUserData().idRole.toString();
    return this.mfListService.getMfList(idCommerce, idRole).pipe(
      map((res: Result<MfListDataModules[]>) => {
        if (res.success && res.value && res.value?.length > 0) {
          this.microFrontsList = res.value.filter((res) => res.active);
          return res.value;
        } else {
          this.microFrontsList = [];
          return [];
        }
      })
    );
  }

  saveOptions(options: MfListDataModules[]): void {
    localStorage.setItem('lealRoutesOptions', JSON.stringify(options));
  }

  getOptions(): MfListDataModules[] {
    this.microFrontsList = JSON.parse(
      localStorage.getItem('lealRoutesOptions') as string
    );
    return this.microFrontsList;
  }

  getMicroFronts() {
    return this.microFrontsList;
  }

  set mfRoutes(routes) {
    this.mfRoutesGenerated = routes;
  }

  get mfRoutes() {
    return this.mfRoutesGenerated;
  }
}
